/*
    The following is a basic but effective approach to dewarping for
    circular fisheye videos. We effectively map the circular image
    onto the inside of a hemisphere (using the arcsin function to
    spread the pixels further at the edges than the centre) and position
    a camera at its centre. We then achieve the feeling of moving around
    the video by either lifting the camera up or down (through adjusting
    its latitude) or rotating the hemisphere. This approach was based on
    https://github.com/yanwsh/videojs-panorama.

    Remember there are browser limits on number of WebGL canvases if
    rendering multiple of this component at once.
*/

import { forwardRef, memo, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from "react";
import _ from 'lodash';
import { LinearFilter, MathUtils, Mesh, MeshBasicMaterial, PerspectiveCamera, RGBAFormat, SRGBColorSpace, Scene, SphereGeometry, Vector3, VideoTexture, WebGLRenderer, Vector2 } from "three";
import PropTypes from 'prop-types';

import useResizeOberserver from "../../../hooks/useResizeObserver";

import { VIEW_SINGLE, VIEW_TRIPLE, VIEW_QUADRANTS, ORIENTATION_CEILING, ORIENTATION_WALL, ORIENTATION_FLOOR } from "../../../utils/dewarping";

// Constants
const INIT_FOV = 95;
const MAX_FOV = 110;
const MIN_FOV = 50;

// We want to restrict latitude for ceiling and floor cameras between 0-80
// However at 0 three.js seems to always treat camera.up as (0,1,0) so we
// actually restrict minimum to slightly above 0.
const MIN_LONGITUDE_LATITUDE = 0.01;
// For wall cameras, latitude has full range of -80 to 80.
const MIN_LONGITUDE_LATITUDE_WALL = -80;
const MAX_LONGITUDE_LATITUDE = 80;

// Radius of sphere onto the inside of which we project the dewarped image
const SPHERE_RADIUS = 500;

const VIDEO_FPS = 10;

// Renders a canvas with a dewarped view of a fisheye video
// Currently video must be circular (width = height). Would need to adjust the geometry to support other aspect ratios
const Dewarper = memo(forwardRef(({ reactPlayerRef, orientation = ORIENTATION_CEILING, view = VIEW_SINGLE, selectedCamera }, ref) => {

    const containerRef = useRef();
    const canvasRef = useRef();
    const threeJsCanvasRef = useRef();
    const rendererRef = useRef();
    const sceneRef = useRef();
    const textureRef = useRef();

    // Put this prop in ref so we don't have to recreate functions below
    const selectedCameraRef = useRef(selectedCamera);
    useMemo(() => {
        selectedCameraRef.current = selectedCamera;
    }, [selectedCamera]);

    // Functions to look around scene
    // These get attached to ref
    const controlsRef = useRef({
        zoom: null,
        moveX: null,
        moveY: null
    });

    useImperativeHandle(ref, () => {
        return {
            canvas: canvasRef.current,
            zoom: (...args) => controlsRef.current.zoom(...args),
            moveX: (...args) => controlsRef.current.moveX(...args),
            moveY: (...args) => controlsRef.current.moveY(...args),
        };
    }, []);
    
    // Callbacks for user actions
    const handleResizeRef = useRef();
    const [handlers, setHandlers] = useState({
        onMouseDown: null,
        onMouseMove: null,
        onMouseUp: null,
        onTouchStart: null,
        onTouchEnd: null
    });
    
    // Need to tell three.js renderer and camera when canvas is resized
    useResizeOberserver(containerRef, ({ width, height}) => {
        handleResizeRef.current(width, height);
    });

    // Set up scene and listeners
    useLayoutEffect(() => {
        
        const width = containerRef.current.offsetWidth;
        const height = containerRef.current.offsetHeight;
        
        const video = reactPlayerRef.current.getInternalPlayer();

        const canvas = canvasRef.current;
        canvas.width = width * devicePixelRatio;
        canvas.height = height * devicePixelRatio;
        
        // Renderer
        const renderer = rendererRef.current = new WebGLRenderer({
            // Need this to take snapshots
            preserveDrawingBuffer: true
        });
        renderer.setPixelRatio(window.devicePixelRatio);
        renderer.setSize(width, height);
        renderer.autoClear = false;
        renderer.setClearColor(0x000000, 1);

        threeJsCanvasRef.current = renderer.domElement;

        // Scene
        const scene = sceneRef.current = new Scene();

        // Texture
        const texture = textureRef.current = new VideoTexture(video);
        texture.colorSpace = SRGBColorSpace;
        texture.generateMipmaps = false;
        texture.minFilter = LinearFilter;
        texture.maxFilter = LinearFilter;
        texture.format = RGBAFormat;

        // Geometry
        const geometry = getGeometry();

        // Mesh
        const material = new MeshBasicMaterial({ map: texture });
        const mesh = new Mesh(geometry, material);
        
        scene.add(mesh);

        return () => {
            // Dispose three.js objects (https://threejs.org/docs/#manual/en/introduction/How-to-dispose-of-objects)
            scene.remove(mesh);
            texture.dispose();
            disposeGeometry();
            material.dispose();
            renderer.dispose();
        };

    }, [reactPlayerRef]);

    // Set up cameras and render scene
    // We split this out from logic above as these things need to change when orientation/view changes
    useLayoutEffect(() => {

        const aspectRatio = containerRef.current.offsetWidth / containerRef.current.offsetHeight;

        const video = reactPlayerRef.current.getInternalPlayer();
        const canvas = canvasRef.current;
        const threeJsCanvas = threeJsCanvasRef.current;
        const renderer = rendererRef.current;
        const scene = sceneRef.current;
        const texture = textureRef.current;

        // Create camera(s) for view

        const cameras = [];

        const createCamera = ({
            longitude = 0,
            latitude = orientation === ORIENTATION_WALL ? 0 : (MAX_LONGITUDE_LATITUDE / 2),
            direction = 0,
            x = 0,
            y = 0,
            width = 1,
            height = 1
        } = {}) => {
            const camera = new PerspectiveCamera(INIT_FOV, aspectRatio, 1, 2000);

            /*
                Aim the camera. Remember (0,0,-1) will be the centre of the hemisphere
                that the dewarped image is projected onto.

                There are three properties here used to calculate the vector on the
                sphere that the camera is aiming at. Longitude and latitude refers to
                a vector on the sphere, but I have also added a "direction" property
                which is number of degrees between 0-360 around the z axis (where
                0 degrees is in the direction [0,1,0]) which the camera direction is
                rotated.

                So the process of calculating which vector on the sphere the camera is
                aiming at is:
                    1. Use longitude and latitude to find a vector on the sphere
                    2. Rotate this vector around the negative z axis by the number of
                    degrees specified by "direction"
                
                The reason for the addition of the "direction" property is it makes it
                easier to calculate the movement of the camera when the user is dragging
                it around. This is because for ceiling and floor orientations any mouse
                dragging on the axis should rotate the camera view rather than tilt it.

                To confirm, camera movements for each mouse axis works as follows:
                    - Ceiling cameras: x axis controls "direction", y axis controls latitude
                                    between 0 and 80
                    - Wall cameras: x axis controls longitude between -80 and 80, y axis
                                    controls latitude between -80 and 80
                    - Floor cameras: x axis controls "direction", y axis controls latitude
                                    between 0 and 80
            */

            switch (orientation) {
                case ORIENTATION_CEILING:
                default:
                    camera.up = new Vector3(0, 0, 1);
                    break;
                case ORIENTATION_WALL:
                    camera.up = new Vector3(0, 1, 0);
                    break;
                case ORIENTATION_FLOOR:
                    camera.up  = new Vector3(0, 0, -1);
                    break;
            }

            cameras.push({
                camera,
                longitude,
                latitude,
                direction,
                x,
                y,
                width,
                height
            });
        }

        switch (view) {
            case VIEW_SINGLE:
            default:
                const size = renderer.getSize(new Vector2());
                renderer.setViewport(0, 0, size.width, size.height);
                
                renderer.setScissorTest(false);
                createCamera();
                break;
            case VIEW_QUADRANTS:
                renderer.setScissorTest(true);
                if (orientation === ORIENTATION_WALL) {
                    const halfMaxLongitudeLatitude = MAX_LONGITUDE_LATITUDE / 2;
                    for (let i = 0; i < 4; i++) {
                        createCamera({
                            longitude: i % 2 === 0 ? -halfMaxLongitudeLatitude : halfMaxLongitudeLatitude,
                            latitude: i < 2 ? halfMaxLongitudeLatitude : -halfMaxLongitudeLatitude,
                            x: i % 2 === 0 ? 0 : 0.5,
                            y: i < 2 ? 0.5 : 0,
                            width: 0.5,
                            height: 0.5
                        });
                    }
                } else {
                    [0, 90, 180, 270].forEach((direction, index) => {
                        createCamera({
                            direction,
                            x: index % 2 === 0 ? 0 : 0.5,
                            y: index < 2 ? 0.5 : 0,
                            width: 0.5,
                            height: 0.5
                        });
                    });
                }
                break;
            case VIEW_TRIPLE:
                renderer.setScissorTest(true);
                if (orientation === ORIENTATION_WALL) {
                    for (let i = 0; i < 3; i++) {
                        createCamera({
                            longitude: -25 + i * 25,
                            latitude: i < 2 ? 25 : -25,
                            x: i % 2 === 0 ? 0.5 : 0,
                            y: i === 0 ? 0.5 : 0,
                            width: 0.5,
                            height: 0.5
                        });
                    }
                } else {
                    [0, 120, 240].forEach((direction, index) => {
                        createCamera({
                            direction,
                            x: index % 2 === 0 ? 0.5 : 0,
                            y: index === 0 ? 0.5 : 0,
                            width: 0.5,
                            height: 0.5
                        });
                    });
                }
                break;
        }

        // Update the canvas for each video frame using requestAnimationFrame
        let lastUpdate = new Date().getTime();
        let animationId;

        // Have we set up the first frame? After that we only need to update texture when frame changes
        let initialFrameTaken = false;
        const updateCanvas = () => {
            renderer.clear();

            const size = renderer.getSize(new Vector2());
            const outputWidth = size.width;
            const outputHeight = size.height;

            cameras.forEach(({
                camera,
                longitude,
                latitude,
                direction,
                x,
                y,
                width,
                height
            }) => {
                // Set camera position
                const longitudeRadians = MathUtils.degToRad(longitude);
                const latitudeRadians = MathUtils.degToRad(latitude);

                // Calculate vector on sphere dictated by longitude and latitude
                const cameraTargetX = SPHERE_RADIUS * Math.cos(latitudeRadians) * Math.sin(longitudeRadians);
                const cameraTargetY = SPHERE_RADIUS * Math.sin(latitudeRadians);
                const cameraTargetZ = -SPHERE_RADIUS * Math.cos(latitudeRadians) * Math.cos(longitudeRadians);

                // Rotate this vector around negative z axis as dictated by direction
                const directionRadians = MathUtils.degToRad(direction);
                const lookingAt = new Vector3(cameraTargetX, cameraTargetY, cameraTargetZ).applyAxisAngle(new Vector3(0, 0, -1), directionRadians);

                camera.lookAt(lookingAt);

                // If rendering multiple cameras, they each only take up part of the canvas
                if (view !== VIEW_SINGLE) {
                    const top = y * outputHeight;
                    const left = x * outputWidth;
                    const renderWidth = width * outputWidth;
                    const renderHeight = height * outputHeight;

                    renderer.setViewport(left, top, renderWidth, renderHeight);
                    renderer.setScissor(left, top, renderWidth, renderHeight);

                    // Need to clear depth buffer so each camera rendered has a clean state and they don't interfere with each other
                    renderer.clearDepth();
                }

                renderer.render(scene, camera);
            });

            const context = canvas.getContext('2d');
            context.drawImage(threeJsCanvas, 0, 0);

            const canvasWidth = canvas.width;
            const canvasHeight = canvas.height;

            if (view === VIEW_TRIPLE) {
                // Draw fisheye
                const quadrantWidth = canvasWidth / 2;
                const quadrantHeight = canvasHeight / 2;

                let drawWidth = quadrantWidth;
                let drawHeight = quadrantHeight;
                let offsetX = 0;
                let offsetY = 0;
                
                const videoAspectRatio = video.videoWidth / video.videoHeight;
                const quadrantAspectRatio = quadrantWidth / quadrantHeight;

                if (videoAspectRatio > quadrantAspectRatio) {
                    // Video is wider than quadrant
                    drawHeight = drawWidth / videoAspectRatio;
                    offsetY = (quadrantHeight - drawHeight) / 2;
                } else {
                    // Video is taller than quadrant
                    drawWidth = drawHeight * videoAspectRatio;
                    offsetX = (quadrantWidth - drawWidth) / 2;
                }

                // Draw black rectange over quadrant as background
                context.fillRect(0, 0, quadrantWidth, quadrantHeight);
                // Draw video frame, scaled and centred
                context.drawImage(video, offsetX, offsetY, drawWidth, drawHeight);
            }

            // Draw outline of selected view
            if (typeof selectedCameraRef.current === 'number') {
                const camera = cameras[selectedCameraRef.current];
                if (camera) {
                    context.strokeStyle = '#4D90FE';
                    context.lineWidth = 4;
                    context.strokeRect(camera.x * canvasWidth, (1- camera.y - camera.height) * canvasHeight, camera.width * canvasWidth, camera.height * canvasHeight);
                }
            }
        }
        const animate = () => {
            animationId = requestAnimationFrame(animate);
            // Only need to update texture if we haven't taken first frame or afterwards if it has changed
            if (!initialFrameTaken || (!video.paused && !video.ended && new Date().getTime() - lastUpdate >= 1000 / VIDEO_FPS)) {
                texture.needsUpdate = true;
                lastUpdate = new Date();
                initialFrameTaken = true;
            }
            
            updateCanvas();
        }

        animate();

        // Function invoked when canvas resized
        handleResizeRef.current = (width, height) => {
            renderer.setSize(width, height);
            cameras.forEach(({ camera }) => {
                camera.aspect = width / height;
                camera.updateProjectionMatrix();
            });
            canvas.width = width * devicePixelRatio;
            canvas.height = height * devicePixelRatio;
            updateCanvas();
        }

        // Functions to control movement
        const zoom = (index, change) => {
            const camera = cameras[index].camera;
            camera.fov = _.clamp(camera.fov + change, MIN_FOV, MAX_FOV);
            camera.updateProjectionMatrix();
        }
        const moveX = (index, change) => {
            const camera = cameras[index];
            // Mouse x axis controls longitude for wall orientations and rotation for ceiling and floor orientations
            if (orientation === ORIENTATION_WALL) {
                camera.longitude = _.clamp(camera.longitude + change, getMinLongitudeLatitude(orientation), MAX_LONGITUDE_LATITUDE);
            } else {
                camera.direction = (camera.direction + change * (orientation === ORIENTATION_FLOOR ? -1 : 1)) % 360;
                if (camera.direction < 0) {
                    camera.direction = 360 + camera.direction;
                }
            }
        }
        const moveY = (index, change) => {
            const camera = cameras[index];
            // Mouse y axis controls latitude for all orientations
            camera.latitude = _.clamp(camera.latitude + change * (orientation === ORIENTATION_FLOOR ? -1 : 1), getMinLongitudeLatitude(orientation), MAX_LONGITUDE_LATITUDE);
        }


        // Identify which camera on the canvas is under a mouse event
        const identifyCameraFromMousePosition = (event) => {
            const canvasRect = canvas.getBoundingClientRect();
            return cameras.find(({ x, y, width, height }) => {
                return (
                    event.clientX >= canvasRect.x + x * canvasRect.width &&
                    event.clientX <= canvasRect.x + (x + width) * canvasRect.width &&
                    event.clientY >= canvasRect.y + (1 - y - height) * canvasRect.height &&
                    event.clientY <= canvasRect.y + (1 - y) * canvasRect.height
                );
            });
        }


        // Zoom on mouse wheel
        const onWheel = event => {
            event.preventDefault();
            const camera = identifyCameraFromMousePosition(event);
            if (camera) {
                zoom(cameras.indexOf(camera), event.deltaY * 0.05);
            }
        }

        // Handle mouse and touch movements
        let mouseDownCamera;

        let onMouseDownMouseX;
        let onMouseDownMouseY;

        let onMouseDownDirection;
        let onMouseDownLongitude;
        let onMouseDownLatitude;
        

        const onMouseDown = event => {

            const camera = identifyCameraFromMousePosition(event);

            if (camera) {
                mouseDownCamera = camera;

                onMouseDownMouseX = event.clientX;
                onMouseDownMouseY = event.clientY;
                
                onMouseDownDirection = mouseDownCamera.direction;
                onMouseDownLongitude = mouseDownCamera.longitude;
                onMouseDownLatitude = mouseDownCamera.latitude;
            }
        }

        const onMouseMove = event => {
            if (mouseDownCamera) {
                if (orientation === ORIENTATION_WALL) {
                    // Mouse x axis controls longitude for wall orientations
                    // 0.2 is used to scale mouse movement to a comfortable level
                    mouseDownCamera.longitude = _.clamp((onMouseDownMouseX - event.clientX) * 0.2 + onMouseDownLongitude, getMinLongitudeLatitude(orientation), MAX_LONGITUDE_LATITUDE);
                } else {
                    // Mouse x axis controls direction for ceiling and floor orientations
                    // 0.17 is used to scale mouse movement to comfortable level
                    mouseDownCamera.direction = ((onMouseDownMouseX - event.clientX) * 0.17 * (orientation === ORIENTATION_FLOOR ? -1 : 1) + onMouseDownDirection) % 360;
                    if (mouseDownCamera.direction < 0) {
                        // Keep it in range 0-360
                        mouseDownCamera.direction = 360 + mouseDownCamera.direction;
                    }
                }

                // Mouse y axis controls latitude for all orientations
                // 0.2 is used to scale mouse movement to a comfortable level
                mouseDownCamera.latitude = _.clamp((event.clientY - onMouseDownMouseY) * 0.2 * (orientation === ORIENTATION_FLOOR ? -1 : 1) + onMouseDownLatitude, getMinLongitudeLatitude(orientation), MAX_LONGITUDE_LATITUDE);
            }
        }

        const onMouseUp = event => {
            mouseDownCamera = null;
            onMouseDownMouseX = null;
            onMouseDownMouseY = null;
            
            onMouseDownDirection = null;
            onMouseDownLongitude = null;
            onMouseDownLatitude = null;
        }

        // Handle touch screens
        let pinching; // Is user using multiple fingers?
        let lastMultiTouchDistance; // If pinching, what is the distance between fingers?

        const getDistanceBetweenTouches = event => Math.hypot(event.touches[1].clientX - event.touches[0].clientX, event.touches[1].clientY - event.touches[0].clientY);

        const onTouchStart = event => {
            if (event.touches.length > 1) {
                pinching = true;
                lastMultiTouchDistance = getDistanceBetweenTouches(event);
            }

            // Add `clientX` and `clientY` to event so we can reuse `onMouseDown`
            event.clientX = event.touches[0].clientX;
            event.clientY = event.touches[0].clientY;
            onMouseDown(event);
        }

        const onTouchMove = event => {
            // Stop screen scrolling or zooming
            event.preventDefault();

            if (pinching) {
                const currentDistance = getDistanceBetweenTouches(event);
                // Mock `deltaY` to reuse `onWheel`
                event.deltaY = (lastMultiTouchDistance - currentDistance) * 2;
                onWheel(event);
                lastMultiTouchDistance = currentDistance;
            } else if (event.touches.length === 1) {
                // Add `clientX` and `clientY` to event so we can reuse `onMouseMove`
                event.clientX = event.touches[0].clientX;
                event.clientY = event.touches[0].clientY;
                onMouseMove(event);
            }
        }

        const onTouchEnd = event => {
            if (event.touches.length === 0) {
                // Reset pinching variables when no touches left
                pinching = false;
                lastMultiTouchDistance = null;
            }
            onMouseUp();
        }

        setHandlers({
            onMouseDown,
            onMouseMove,
            onMouseUp,
            onTouchStart,
            onTouchEnd
        });

        // These listeners need to be added manually as they must not be passive to prevent default
        canvas.addEventListener('wheel', onWheel, { passive: false });
        canvas.addEventListener('touchmove', onTouchMove, { passive: false });

        controlsRef.current.zoom = zoom;
        controlsRef.current.moveX = moveX;
        controlsRef.current.moveY = moveY;

        return () => {
            cancelAnimationFrame(animationId);
            canvas.removeEventListener('wheel', onWheel, { passive: false });
            canvas.removeEventListener('touchmove', onTouchMove, { passive: false });
        };

    }, [reactPlayerRef, orientation, view]);


    return (
        <div
            ref={containerRef}
            style={{
                width: '100%',
                height: '100%',
                // border: borderColour ? `1px solid ${borderColour}` : undefined
            }}
        >
            <canvas
                ref={canvasRef}
                style={{
                    width: '100%',
                    height: '100%'
                }}
                onMouseDown={handlers.onMouseDown}
                onMouseUp={handlers.onMouseUp}
                onMouseMove={handlers.onMouseMove}
                onTouchStart={handlers.onTouchStart}
                onTouchEnd={handlers.onTouchEnd}
            />
        </div>
    );
}));

Dewarper.propTypes = {
    /** Ref of a ReactPlayer. */
    reactPlayerRef: PropTypes.object.isRequired,
    /** Orientation of camera which recorded footage. */
    orientation: PropTypes.oneOf([ORIENTATION_CEILING, ORIENTATION_WALL, ORIENTATION_FLOOR]),
    /** View to render. */
    view: PropTypes.oneOf([VIEW_SINGLE, VIEW_QUADRANTS, VIEW_TRIPLE]),
    /** Draw outline around one of the camera views being rendered. Max index is 3 as quadrant view has four cameras. */
    selectedCamera: PropTypes.number
};

/* -------------------------------- Geometry -------------------------------- */

// Reuse three.js geometry to improve performance
// (This will start making a difference when showing multiple dewarped canvases at once)

// Current geometry
let geometry = null;
// Number of Dewarper components using the geometry
let geometryUsers = 0;

const getGeometry = () => {
    geometryUsers++;
    if (!geometry) {
        // Create geometry if we don't have one cached

        // The geometry is a sphere as we'll map the circular video frames onto the inside of half of it
        geometry = new SphereGeometry(SPHERE_RADIUS, 60, 40).toNonIndexed();

        // This maths, taken from https://github.com/yanwsh/videojs-panorama, handles the mapping
        // which dewarps the image.
        // I roughly understood it when I first started implementing our own dewarping a few months
        // ago but can't remember the specifics now to write a decent comment.
        // The gist is that it uses the arcsin function to map pixels in the warped frame onto the inside
        // of the hemisphere, with pixels furthest from the centre being moved the most (as fisheye
        // warping is greatest at the edges).
        const normals = geometry.attributes.normal.array;
        const uvs = geometry.attributes.uv.array;
        for (let i = 0, l = normals.length / 3; i < l; i++) {
            const x = normals[i * 3 + 0];
            const y = normals[i * 3 + 1];
            const z = normals[i * 3 + 2];

            let r = Math.asin(Math.sqrt(x * x + z * z) / Math.sqrt(x * x + y * y + z * z)) / Math.PI; // Range 0 - 0.5
            if (y < 0) {
                r = 1 - r;
            }
            
            let theta = x === 0 && z === 0 ? 0 : Math.acos(x / Math.sqrt(x * x + z * z)); // Range 0 - pi
            if (z < 0) {
                theta = theta * -1;
            }
            
            uvs[i * 2 + 0] = -1 * r * Math.cos(theta) + 0.5;
            uvs[i * 2 + 1] = 1 * r * Math.sin(theta) + 0.5;
        }

        // Image centre will be in direction (0, 0, -1)
        geometry.rotateX(-Math.PI/2);
        geometry.scale(-1, 1, 1);
    }
    return geometry;
}

const disposeGeometry = () => {
    geometryUsers--;
    if (geometryUsers === 0) {
        // If there are no components left using the geometry, we must dispose it
        geometry.dispose();
        geometry = null;
    }
}

/* ---------------------------- Utility functions --------------------------- */

const getMinLongitudeLatitude = orientation => {
    return orientation === ORIENTATION_WALL ? MIN_LONGITUDE_LATITUDE_WALL : MIN_LONGITUDE_LATITUDE;
}


export default Dewarper;